import { Button } from "@/components/ui/button";
import { ArrowRight, Play, CheckCircle, TrendingUp, Users, Zap } from "lucide-react";

interface HeroProps {
  onApplyClick: () => void;
  onLearnMoreClick: () => void;
}

const Hero = ({ onApplyClick, onLearnMoreClick }: HeroProps) => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
      
      {/* Floating decorative elements */}
      <div className="absolute top-32 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-float-slow" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl" />
      
      {/* Grid pattern */}
      <div 
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
        style={{
          backgroundImage: `radial-gradient(hsl(var(--primary)) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}
      />

      <div className="container-wide relative z-10 py-16 lg:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8 animate-fade-up">
                <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-sm font-semibold text-primary">Revenue Engineering & Growth Systems</span>
              </div>

              {/* Headline */}
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-6 animate-fade-up animation-delay-100">
                We Install{" "}
                <span className="gradient-text">Revenue Systems</span>
                {" "}That Increase Cash Flow in{" "}
                <span className="text-accent">90 Days</span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg sm:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-8 animate-fade-up animation-delay-200 text-balance leading-relaxed">
                For established businesses stuck with inconsistent leads, low close rates, 
                and founder-dependent growth. We engineer predictable revenue.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-up animation-delay-300">
                <Button variant="hero" size="xl" onClick={onApplyClick} className="group">
                  Apply for Revenue Diagnostic
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <Button variant="heroOutline" size="xl" onClick={onLearnMoreClick} className="group">
                  <Play className="w-5 h-5" />
                  See How It Works
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="mt-12 flex flex-wrap items-center justify-center lg:justify-start gap-6 animate-fade-up animation-delay-400">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>90-Day Installation</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>ROI-Focused</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  <span>$30K-$300K/mo Businesses</span>
                </div>
              </div>
            </div>

            {/* Right Content - Stats Cards */}
            <div className="relative animate-fade-up animation-delay-400">
              <div className="grid grid-cols-2 gap-4">
                {/* Main Card */}
                <div className="col-span-2 p-6 rounded-3xl bg-card border border-border shadow-lg card-hover">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-2xl gradient-primary flex items-center justify-center shadow-blue">
                      <TrendingUp className="w-7 h-7 text-primary-foreground" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Average Revenue Increase</p>
                      <p className="text-3xl font-bold font-display text-foreground">+32%</p>
                    </div>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full w-3/4 gradient-primary rounded-full" />
                  </div>
                </div>

                {/* Secondary Cards */}
                <div className="p-5 rounded-2xl bg-card border border-border shadow-md card-hover animate-float">
                  <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center mb-3">
                    <Users className="w-6 h-6 text-secondary" />
                  </div>
                  <p className="text-2xl font-bold font-display text-foreground">150+</p>
                  <p className="text-sm text-muted-foreground">Businesses Scaled</p>
                </div>

                <div className="p-5 rounded-2xl bg-card border border-border shadow-md card-hover animate-float animation-delay-200">
                  <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center mb-3">
                    <Zap className="w-6 h-6 text-accent" />
                  </div>
                  <p className="text-2xl font-bold font-display text-foreground">90</p>
                  <p className="text-sm text-muted-foreground">Day Implementation</p>
                </div>
              </div>

              {/* Floating badge */}
              <div className="absolute -top-4 -right-4 px-4 py-2 rounded-full bg-accent text-accent-foreground font-semibold text-sm shadow-orange animate-float-slow">
                No Risk Guarantee
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-xs text-muted-foreground">Scroll to explore</span>
        <div className="w-6 h-10 rounded-full border-2 border-primary/30 flex justify-center pt-2">
          <div className="w-1.5 h-3 rounded-full bg-primary animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
